/**
 * Current "viewpoint" of a case — the transient exploration or the active
 * saved investigation — reduced to what chrome needs to render.
 *
 * Lives in the feature layer because it reads the per-case investigations
 * store: UI components (top-bar chip, case rail, breadcrumb) consume this hook
 * instead of touching the store directly.
 */

import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { InvestigationMode } from '@/components/investigations/_internal/types'
import {
  getOrCreateInvestigationsStore,
  type InvestigationsState,
} from '@/stores/investigations-store'

export interface ViewpointSnapshot {
  mode: InvestigationMode
  investigationName?: string
  entityCount: number
  snippetCount: number
  edgeCount: number
  noteCount: number
  activeId?: string
  /** When the active saved investigation was last written (ISO-8601). */
  updatedAt?: string
  isTransient: boolean
  includedInReport: boolean
  readOnly: boolean
}

/** Marker note author used to store an investigation's description — never a
 * regular note, so it must not count towards `noteCount`. */
const DESCRIPTION_NOTE_AUTHOR = '__investigation_description__'

/** Sentinel store used when no case is in scope, so the hook can always call
 * `useStore` (rules of hooks) without spinning up real machinery. */
const NOOP_STORE_CASE_ID = '__viewpoint_noop__'

const EMPTY_SNAPSHOT: ViewpointSnapshot = {
  mode: 'empty',
  entityCount: 0,
  snippetCount: 0,
  edgeCount: 0,
  noteCount: 0,
  isTransient: false,
  includedInReport: false,
  readOnly: false,
}

/** Pure reduction of the store state — exported for tests and non-hook callers. */
export function deriveViewpointSnapshot(s: InvestigationsState): ViewpointSnapshot {
  if (s.transient) {
    return {
      mode: 'exploration',
      investigationName: s.transient.suggestedTitle,
      entityCount: s.transient.graphView?.nodes?.length ?? 0,
      snippetCount: s.transient.graphView.snippets?.length ?? 0,
      edgeCount: s.transient.graphView?.edges?.length ?? 0,
      noteCount: 0,
      isTransient: true,
      includedInReport: false,
      readOnly: false,
    }
  }
  if (s.activeId) {
    const active = s.saved.find(i => i.id === s.activeId)
    if (active) {
      return {
        mode: s.dirty ? 'modified' : 'saved',
        investigationName: active.title,
        entityCount: active.graphView?.nodes?.length ?? 0,
        snippetCount: active.graphView.snippets?.length ?? 0,
        edgeCount: active.graphView?.edges?.length ?? 0,
        noteCount: active.notes.filter(n => n.authorName !== DESCRIPTION_NOTE_AUTHOR).length,
        activeId: s.activeId,
        updatedAt: active.updatedAt,
        isTransient: false,
        includedInReport: active.includedInReport ?? false,
        readOnly: active.readOnly ?? false,
      }
    }
  }
  return EMPTY_SNAPSHOT
}

/**
 * Subscribe to the case's current viewpoint. Safe to call outside
 * `<InvestigationsStoreProvider>` — the per-case store is module-level cached,
 * which is what lets app chrome (rail, top bar) read it.
 */
export function useViewpointSnapshot(caseId: string | undefined): ViewpointSnapshot {
  const storeApi = getOrCreateInvestigationsStore({ caseId: caseId ?? NOOP_STORE_CASE_ID })
  return useStore(storeApi, useShallow(deriveViewpointSnapshot))
}
